"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isFormValid = exports.setupFormValidation = exports.validateElement = exports.setCustomValidation = exports.setHandleFocus = exports.setCreateErrorElement = void 0;
/* eslint-disable no-param-reassign */
const email_address_parser_1 = require("@brainstation/email-address-parser");
const LINKED_ERROR_ELEMENT = Symbol('linked-error-element');
const CREATE_ERROR_ELEMENT = Symbol('create-error-element');
const HANDLE_FOCUS = Symbol('handle-focus');
const hasValidated = Symbol('has-validated');
const isValid = Symbol('is-valid');
const alreadySetup = Symbol('already-setup');
function setCreateErrorElement(element, handler) {
    element[CREATE_ERROR_ELEMENT] = handler;
}
exports.setCreateErrorElement = setCreateErrorElement;
function setHandleFocus(element, handler) {
    element[HANDLE_FOCUS] = handler;
}
exports.setHandleFocus = setHandleFocus;
function setCustomValidation(element, func) {
    element.customValidation = func;
}
exports.setCustomValidation = setCustomValidation;
function isRadio(element) {
    return element.tagName === 'INPUT' && element.name && element.type === 'radio' && element.form;
}
function getFirstRadio(element) {
    // @ts-ignore
    return element.form.querySelector(`input[type="radio"][name="${element.name}"]`);
}
function isFirstRadio(element) {
    return isRadio(element) && getFirstRadio(element) === element;
}
function defaultCreateErrorElement(element, message) {
    var _a;
    const displayAfter = isRadio(element) ? (_a = element.closest('fieldset')) !== null && _a !== void 0 ? _a : element : element;
    const errorElement = document.createElement('div');
    errorElement.className = 'field-validation-error';
    errorElement.id = element.id + '-error';
    errorElement.innerHTML = message;
    displayAfter.insertAdjacentElement('afterend', errorElement);
    return errorElement;
}
function setErrorMessage(element, message = element.validationMessage) {
    var _a;
    // avoid radio groups showing duplicate messages
    if (isRadio(element) && !isFirstRadio(element)) {
        return;
    }
    if (element.dataset.msgRequired && element.validity.valueMissing) {
        message = element.dataset.msgRequired;
    }
    else if (element.dataset.valRequired && element.validity.valueMissing) {
        message = element.dataset.valRequired;
    }
    else if (element.dataset.valEmail && element.validity.typeMismatch) {
        message = element.dataset.valEmail;
    }
    else if (element.dataset.valLength && (element.validity.tooLong || element.validity.tooShort)) {
        message = element.dataset.valLength;
    }
    else if (element.dataset.valNumber && element.validity.badInput) {
        message = element.dataset.valNumber;
    }
    else if (element.dataset.valDate && element.validity.badInput) {
        message = element.dataset.valDate;
    }
    else if (element.dataset.valRegex && element.validity.patternMismatch) {
        message = element.dataset.valRegex;
    }
    else if (element.dataset.valUrl && element.validity.typeMismatch) {
        message = element.dataset.valUrl;
    }
    else if (element.dataset.msg && !element.validity.valid) {
        message = element.dataset.msg;
    }
    element.classList.toggle('error', !!message);
    const errorElement = element[LINKED_ERROR_ELEMENT];
    if (errorElement && !message) {
        errorElement.remove();
        delete element[LINKED_ERROR_ELEMENT];
    }
    else if (errorElement && message) {
        errorElement.innerHTML = message;
    }
    else if (!errorElement && message) {
        const createErrorElement = element[CREATE_ERROR_ELEMENT] && typeof element[CREATE_ERROR_ELEMENT] === 'function'
            ? element[CREATE_ERROR_ELEMENT]
            : ((_a = element.form) === null || _a === void 0 ? void 0 : _a[CREATE_ERROR_ELEMENT]) &&
                typeof element.form[CREATE_ERROR_ELEMENT] === 'function'
                ? element.form[CREATE_ERROR_ELEMENT]
                : defaultCreateErrorElement;
        element[LINKED_ERROR_ELEMENT] = createErrorElement(element, message);
    }
}
function validateElement(element, canFocus = false) {
    var _a;
    element.setCustomValidity('');
    element[hasValidated] = true;
    let didFocus = false;
    // allow validating hidden file inputs since we always hide the actual input
    // also run custom validation on hidden inputs
    if ((element.tagName !== 'INPUT' ||
        (element.type !== 'file' && (element.type !== 'hidden' || !element.customValidation))) &&
        !('validateHidden' in element.dataset) &&
        (element.hidden || !element.checkVisibility() || 'ignoreValidation' in element.dataset)) {
        return [true, didFocus];
    }
    const isEmail = element.type === 'email' || element.inputMode === 'email';
    // switch to text input with email mode for keyboard and autocomplete reasons
    // want to suppress built in email regex but maintain the editing experience
    if (isEmail && 'inputMode' in element) {
        element.type = 'text';
        element.inputMode = 'email';
    }
    let isValid = element.checkValidity();
    if (isEmail && isValid) {
        isValid = (0, email_address_parser_1.isValidEmail)(element.value || '', { disallowTldAsHostname: true });
        if (!isValid) {
            element.setCustomValidity('Please enter a valid email address.');
        }
    }
    if (!isValid || !element.customValidation || typeof element.customValidation !== 'function') {
        if (!isValid && canFocus) {
            element.scrollIntoView({ block: 'center', behavior: 'smooth' });
            if (element[HANDLE_FOCUS] && typeof element[HANDLE_FOCUS] === 'function') {
                element[HANDLE_FOCUS]();
            }
            else {
                element.focus();
            }
            didFocus = true;
        }
        setErrorMessage(element);
        return [isValid, didFocus];
    }
    // @ts-ignore
    const result = element.customValidation();
    if (!result) {
        throw new Error('Invalid validation state');
    }
    // eslint-disable-next-line prefer-const
    let [validationState, validationMessage] = result;
    if (validationState && element.type === 'email') {
        validationState = (0, email_address_parser_1.isValidEmail)(element.value || '', { disallowTldAsHostname: true });
    }
    if (!validationState) {
        element.setCustomValidity((_a = validationMessage !== null && validationMessage !== void 0 ? validationMessage : element.dataset.msg) !== null && _a !== void 0 ? _a : 'Please enter a valid input.');
        if (canFocus) {
            element.scrollIntoView({ block: 'center', behavior: 'smooth' });
            if (element[HANDLE_FOCUS] && typeof element[HANDLE_FOCUS] === 'function') {
                element[HANDLE_FOCUS]();
            }
            else {
                element.focus();
            }
            didFocus = true;
        }
    }
    // NOTE: element.validationMessage seems bugged for type="hidden" so pass in manually as well here
    setErrorMessage(element, validationMessage);
    return [validationState, didFocus];
}
exports.validateElement = validateElement;
function setupFormValidation(form) {
    if (!form) {
        console.warn("Missing form, so can't setup validation.");
        return;
    }
    if (form[alreadySetup]) {
        return;
    }
    form[alreadySetup] = true;
    form.noValidate = true; // prevent html5 validation from taking over
    form.addEventListener('input', (e) => __awaiter(this, void 0, void 0, function* () {
        const target = e.target;
        if (target === null || target === void 0 ? void 0 : target[hasValidated]) {
            validateElement(target, false);
            if (isRadio(target) && !isFirstRadio(target)) {
                validateElement(getFirstRadio(target), false);
            }
        }
    }));
    form.addEventListener('change', (e) => __awaiter(this, void 0, void 0, function* () {
        const target = e.target;
        if (target === null || target === void 0 ? void 0 : target[hasValidated]) {
            validateElement(target, false);
            if (isRadio(target) && !isFirstRadio(target)) {
                validateElement(getFirstRadio(target), false);
            }
        }
    }));
    form.addEventListener('bstn:change', (e) => __awaiter(this, void 0, void 0, function* () {
        const target = e.target;
        if (target === null || target === void 0 ? void 0 : target[hasValidated]) {
            validateElement(target, false);
        }
    }));
    form.validate = () => {
        try {
            let hasFocused = false;
            let formValid = true;
            for (const element of [...form.elements]) {
                const [elementValid, elementFocused] = validateElement(element, !hasFocused);
                hasFocused = hasFocused || elementFocused;
                formValid = formValid && elementValid;
            }
            form[isValid] = formValid;
            return formValid;
        }
        catch (err) {
            console.error(err);
            return false;
        }
    };
    form.addEventListener('submit', (e) => {
        if (e.submitter) {
            if (e.submitter.formNoValidate) {
                return;
            }
            // safari < 15.1
        }
        else {
            for (const submitButton of form.querySelectorAll('button[type="submit"]')) {
                if (submitButton.formNoValidate) {
                    return;
                }
            }
        }
        const isValid = form.validate();
        if (!isValid) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    }, { capture: true });
}
exports.setupFormValidation = setupFormValidation;
function isFormValid(formElement) {
    // not setup
    if (!(formElement === null || formElement === void 0 ? void 0 : formElement.validate)) {
        return true;
    }
    return formElement.validate();
}
exports.isFormValid = isFormValid;
//# sourceMappingURL=constraint-validation.js.map